import { SupportPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useAsyncThrottle } from "@redotech/react-util/debounce";
import { useHandler } from "@redotech/react-util/hook";
import { useWaitForPrevious } from "@redotech/react-util/wait-for-previous";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { getConversationsCounts } from "@redotech/redo-merchant-app-common/client/team";
import { MerchantAppTopic } from "@redotech/redo-merchant-app-common/events/merchant-app-event-server";
import { MerchantAppEventServerContext } from "@redotech/redo-merchant-app-common/events/merchant-app-event-server-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { useStreamingEnabled } from "@redotech/redo-merchant-fulfillment-app/common/streaming-enabled-context";
import { FrontendTeamNoticeEvent } from "@redotech/redo-notifications-common/team-notifications";
import { ConversationFiltersV3 } from "@redotech/redo-support-common/conversation-filters/conversation-filters";
import { sinkPromise } from "@redotech/util/promise";
import { createContext, memo, useContext, useEffect, useState } from "react";
import { maybeRunConversationsRefresh } from "../handle-notice-event";
import { getPresetViewFilters } from "../utils";
import { ViewsContext } from "../views";

export type ConversationCounts = Record<string, number>;
export const ConversationCountsContext = createContext<
  ConversationCounts | undefined
>(undefined);

export const ConversationCountsService = memo(
  function ConversationCountsService({
    children,
  }: {
    children: React.ReactNode;
  }) {
    const user = useRequiredContext(UserContext);
    const team = useRequiredContext(TeamContext);
    const client = useRequiredContext(RedoMerchantClientContext);
    const eventServer = useRequiredContext(MerchantAppEventServerContext);
    const customViews = useContext(ViewsContext);
    const streamingEnabled = useStreamingEnabled();
    const [conversationCounts, setConversationCounts] = useState<
      Record<string, number>
    >({});
    const supportEnabled = team?.settings.support?.enabled;

    const hasSupportPermission = useHasPermission(
      SupportPermissions.VIEW_SUPPORT,
    );

    const shouldLoadConversationCounts =
      streamingEnabled &&
      supportEnabled &&
      user &&
      client &&
      customViews &&
      hasSupportPermission;
    const builtInViewNames: readonly string[] = [
      "all",
      "assigned",
      "mentions",
      "drafts",
    ];

    const fetchConversationCounts = useHandler(async () => {
      if (!shouldLoadConversationCounts) {
        return;
      }

      const presetViewFilters = builtInViewNames.reduce(
        (acc, viewName) => {
          const filters = getPresetViewFilters(viewName, user);
          if (filters) {
            acc[viewName] = filters;
          }
          return acc;
        },
        {} as Record<string, ConversationFiltersV3>,
      );

      const conversationCounts = await getConversationsCounts(client, {
        views: customViews,
        presetViewFilters,
      });

      setConversationCounts(conversationCounts);
    });

    const throttledFetchConversationCounts = useWaitForPrevious(
      fetchConversationCounts,
    );

    const throttledFetchFromNotifications = useAsyncThrottle(
      throttledFetchConversationCounts,
      Temporal.Duration.from({ seconds: 2 }),
    );

    const fetchConversationCountsIgnoreResult = useHandler(() => {
      sinkPromise(throttledFetchFromNotifications());
    });

    useEffect(() => {
      if (shouldLoadConversationCounts) {
        sinkPromise(fetchConversationCounts());
      }
      // FIXME
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customViews?.length, streamingEnabled]);

    useEffect(() => {
      if (typeof Notification !== "undefined") {
        void Notification.requestPermission();
      }
      if (shouldLoadConversationCounts) {
        const unlistenCallback = eventServer.subscribeAndCallOnce({
          topic: MerchantAppTopic.TEAM,
          callback: async (message: FrontendTeamNoticeEvent | null) => {
            maybeRunConversationsRefresh(
              message,
              fetchConversationCountsIgnoreResult,
            );
          },
        });
        return () => unlistenCallback();
      }
      return undefined;
      // FIXME
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supportEnabled, user, client, customViews, streamingEnabled]);

    return (
      <ConversationCountsContext.Provider value={conversationCounts}>
        {children}
      </ConversationCountsContext.Provider>
    );
  },
);
